/**
 * Unterschiede zwischen zwei Texten bzw. zwei Mail-Versionen — für die
 * Ansicht „Änderungen nachverfolgen“ und den Versionsvergleich.
 */
import { struktursignatur, textbloecke, type BlockSchluessel } from './bloecke';

export interface DiffTeil { typ: 'gleich' | 'weg' | 'neu'; text: string }

export interface BlockUnterschied {
  typ: 'geaendert' | 'neu' | 'entfernt';
  vorher?: string;
  nachher?: string;
  teile?: DiffTeil[];
  block_vorher?: BlockSchluessel;
  block_nachher?: BlockSchluessel;
  /** Text gleich, aber Formatierung/Umbrüche geändert. */
  format?: boolean;
}

/** Mehr Tokens (nach Abzug gleicher Anfänge/Enden) → ganzer Ersatz statt Wortvergleich. */
const GRENZE = 3000;

const WORT = /\s+|[\p{L}\p{N}\p{M}_]+|[^\s\p{L}\p{N}\p{M}_]/gu;

/** Wörter, Leerraum und einzelne Satzzeichen. */
export function woerter(text: string): string[] {
  return text.match(WORT) ?? [];
}

type Op = 0 | 1 | 2; // 0 gleich, 1 nur in a, 2 nur in b

/** Längste gemeinsame Teilfolge; null, wenn zu groß. */
function lcs<T>(a: T[], b: T[]): Op[] | null {
  const n = a.length;
  const m = b.length;
  if (n > GRENZE || m > GRENZE) return null;
  const breite = m + 1;
  const dp = new Uint16Array((n + 1) * breite);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i * breite + j] = a[i] === b[j]
        ? dp[(i + 1) * breite + j + 1] + 1
        : Math.max(dp[(i + 1) * breite + j], dp[i * breite + j + 1]);
    }
  }
  const ops: Op[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { ops.push(0); i++; j++; }
    else if (dp[(i + 1) * breite + j] >= dp[i * breite + j + 1]) { ops.push(1); i++; }
    else { ops.push(2); j++; }
  }
  while (i++ < n) ops.push(1);
  while (j++ < m) ops.push(2);
  return ops;
}

/** Gleicher Anfang und gleiches Ende abtrennen (spart die Tabelle). */
function raender<T>(a: T[], b: T[]): [number, number] {
  let anf = 0;
  while (anf < a.length && anf < b.length && a[anf] === b[anf]) anf++;
  let ende = 0;
  while (ende < a.length - anf && ende < b.length - anf && a[a.length - 1 - ende] === b[b.length - 1 - ende]) ende++;
  return [anf, ende];
}

interface Gruppe { weg: string; neu: string }
type Stueck = string | Gruppe; // string = gleich

/** Zusammenfassen: je Änderungsstelle erst „weg“, dann „neu“; Leerraum zwischen zwei Stellen schlucken. */
function aufraeumen(stuecke: Stueck[]): DiffTeil[] {
  const s: Stueck[] = [];
  for (const x of stuecke) {
    const letzt = s[s.length - 1];
    if (typeof x === 'string') {
      if (typeof letzt === 'string') s[s.length - 1] = letzt + x; else s.push(x);
    } else if (letzt !== undefined && typeof letzt !== 'string') {
      letzt.weg += x.weg;
      letzt.neu += x.neu;
    } else s.push({ ...x });
  }
  for (let k = 1; k < s.length - 1; k++) {
    const mitte = s[k];
    const a = s[k - 1];
    const b = s[k + 1];
    if (typeof mitte === 'string' && /^\s+$/.test(mitte) && typeof a !== 'string' && typeof b !== 'string') {
      s.splice(k - 1, 3, { weg: a.weg + mitte + b.weg, neu: a.neu + mitte + b.neu });
      k = Math.max(0, k - 2);
    }
  }
  const out: DiffTeil[] = [];
  for (const x of s) {
    if (typeof x === 'string') { if (x) out.push({ typ: 'gleich', text: x }); continue; }
    if (x.weg) out.push({ typ: 'weg', text: x.weg });
    if (x.neu) out.push({ typ: 'neu', text: x.neu });
  }
  return out;
}

/**
 * Wortgenauer Vergleich: `gleich` + `weg` ergibt a, `gleich` + `neu` ergibt b.
 * Bei mehr als 3000 abweichenden Tokens: ganzer Ersatz.
 */
export function wortDiff(a: string, b: string): DiffTeil[] {
  if (a === b) return a ? [{ typ: 'gleich', text: a }] : [];
  const x = woerter(a);
  const y = woerter(b);
  const [anf, ende] = raender(x, y);
  const mx = x.slice(anf, x.length - ende);
  const my = y.slice(anf, y.length - ende);
  const stuecke: Stueck[] = [x.slice(0, anf).join('')];
  const ops = lcs(mx, my);
  if (!ops) stuecke.push({ weg: mx.join(''), neu: my.join('') });
  else {
    let i = 0;
    let j = 0;
    for (const op of ops) {
      if (op === 0) { stuecke.push(mx[i++]); j++; }
      else if (op === 1) stuecke.push({ weg: mx[i++], neu: '' });
      else stuecke.push({ weg: '', neu: my[j++] });
    }
  }
  stuecke.push(x.slice(x.length - ende).join(''));
  return aufraeumen(stuecke);
}

function parsen(html: string): Document {
  return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * Vergleicht zwei Fassungen einer Mail Block für Block: gleiche Blocktexte
 * werden ausgerichtet (längste gemeinsame Folge), dazwischen werden ungleiche
 * Nachbarn als „geändert“ gepaart, der Rest ist „neu“ bzw. „entfernt“.
 * Gleicher Text mit anderer Formatierung (fett weg, Umbruch) → „geändert“ mit format: true.
 */
export function bloeckeVergleichen(htmlA: string, htmlB: string): BlockUnterschied[] {
  const a = textbloecke(parsen(htmlA));
  const b = textbloecke(parsen(htmlB));
  const ta = a.map((x) => x.text);
  const tb = b.map((x) => x.text);
  const [anf, ende] = raender(ta, tb);
  const ops = lcs(ta.slice(anf, ta.length - ende), tb.slice(anf, tb.length - ende))
    ?? [...Array(ta.length - anf - ende).fill(1), ...Array(tb.length - anf - ende).fill(2)] as Op[];
  const out: BlockUnterschied[] = [];
  let i = anf;
  let j = anf;
  let weg: number[] = [];
  let neu: number[] = [];
  const luecke = () => {
    const paare = Math.min(weg.length, neu.length);
    for (let k = 0; k < paare; k++) {
      const v = a[weg[k]];
      const w = b[neu[k]];
      out.push({ typ: 'geaendert', vorher: v.text, nachher: w.text, teile: wortDiff(v.text, w.text), block_vorher: v.schluessel, block_nachher: w.schluessel });
    }
    for (const k of weg.slice(paare)) out.push({ typ: 'entfernt', vorher: a[k].text, block_vorher: a[k].schluessel });
    for (const k of neu.slice(paare)) out.push({ typ: 'neu', nachher: b[k].text, block_nachher: b[k].schluessel });
    weg = [];
    neu = [];
  };
  const formatPruefen = (v: number, w: number) => {
    if (struktursignatur(a[v].el) === struktursignatur(b[w].el)) return;
    out.push({ typ: 'geaendert', vorher: a[v].text, nachher: b[w].text, teile: a[v].text ? [{ typ: 'gleich', text: a[v].text }] : [], block_vorher: a[v].schluessel, block_nachher: b[w].schluessel, format: true });
  };
  for (let k = 0; k < anf; k++) formatPruefen(k, k);
  for (const op of ops) {
    if (op === 0) { luecke(); formatPruefen(i, j); i++; j++; }
    else if (op === 1) weg.push(i++);
    else neu.push(j++);
  }
  luecke();
  for (let k = 0; k < ende; k++) formatPruefen(a.length - ende + k, b.length - ende + k);
  return out;
}
